/**
 * How audit actions are shown to people.
 *
 * The database stores stable machine verbs (`partner.credentials_rotated`)
 * because those must never change once written — renaming an action would break
 * every historical query and quietly orphan the rows already recorded. The
 * translation to English happens here, at the edge, and never in the database.
 *
 * **This lives in `shared/` rather than in a feature folder because two features
 * need it** — the dashboard's activity feed and the audit log screen. It started
 * in `features/dashboard/dashboardConstants.js`; the moment the audit screen was
 * built, a `features/ -> features/` import would have been required, which
 * architecture rule 2 forbids. Moving it here was the fix.
 */

/** Machine verb -> what a person reads. Keys mirror `AUDIT` in the backend. */
export const ACTION_LABELS = {
  'auth.login_succeeded': 'Signed in',
  'auth.login_failed': 'Failed sign-in',
  'auth.login_blocked_locked': 'Sign-in blocked — account locked',
  'auth.logout': 'Signed out',
  'auth.token_refreshed': 'Session refreshed',
  'auth.refresh_reuse_detected': 'Refresh token reuse detected',
  'auth.password_reset_requested': 'Password reset requested',
  'auth.password_reset_completed': 'Password reset completed',
  'auth.password_changed': 'Password changed',

  'partner.created': 'Partner added',
  'partner.updated': 'Partner edited',
  'partner.status_changed': 'Partner status changed',
  'partner.credentials_rotated': 'Partner credentials rotated',
  'partner_type.created': 'Partner category added',
  'partner_type.updated': 'Partner category edited',
  'partner_type.status_changed': 'Partner category status changed',

  'client.onboarded': 'Client onboarded',
  'client.updated': 'Client edited',
  'client.status_changed': 'Client status changed',
  'client.subdomain_changed': 'Client subdomain changed',
  'client.partner_mapping_changed': 'Client partner mapping changed',

  'security.thresholds_updated': 'Limits updated',
  'security.threshold_cleared': 'Limit cleared',
  'security.usage_reset': 'Usage counters reset',

  'qr_template.uploaded': 'QR design uploaded',
  'qr_template.activated': 'QR design activated',
  'qr_template.deactivated': 'QR design deactivated',

  'feature.created': 'Feature added to catalog',
  'feature.updated': 'Feature edited',
  'feature.retired': 'Feature retired',
  'feature.restored': 'Feature restored',
  'feature.tenant_toggles_updated': 'Client features updated',
};

/**
 * Actions worth showing in a warning tone. Deliberately short — if everything is
 * highlighted, nothing is. These are the ones that mean somebody should look.
 */
export const ALARMING_ACTIONS = new Set([
  'auth.login_failed',
  'auth.login_blocked_locked',
  'auth.refresh_reuse_detected',
]);

/**
 * The actions that change money-adjacent configuration.
 *
 * Rendered with emphasis on the audit screen, because these are what an incident
 * review is actually looking for in a page of sign-ins.
 */
export const SENSITIVE_ACTIONS = new Set([
  'partner.credentials_rotated',
  'partner.status_changed',
  'security.thresholds_updated',
  'security.threshold_cleared',
  'security.usage_reset',
  'client.subdomain_changed',
  'feature.retired',
]);

/** Falls back to the raw verb rather than hiding an action we forgot to label. */
export function actionLabel(action) {
  return ACTION_LABELS[action] ?? action;
}

/** Human label for the entity a row acted on. */
export function entityLabel(entity) {
  return (
    {
      partner: 'Partner',
      partner_type: 'Partner category',
      client: 'Client',
      feature: 'Feature',
      qr_template: 'QR design',
    }[entity] ?? entity
  );
}

/** "3 min ago" — good enough for an activity feed, and needs no dependency. */
export function relativeTime(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days} d ago`;

  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** Full timestamp for the audit table, where precision matters more than brevity. */
export function absoluteTime(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
